import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  IconButton,
  Badge,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Button,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab,
  Stack,
  Chip,
} from '@mui/material';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import { ThemeProvider } from '@mui/material/styles';
import { GlobalSidePanel } from '../DesignSystem/GlobalSidePanel';
import {
  PrimaryButton,
  SecondaryButton,
} from '../CustomComponents/DesignSystemButtons';
import { EmptyState } from '../CustomComponents/EmptyState';
import {
  designColors,
  designWeights,
  withDesignFont,
} from '../../services/constants/designTokens';
import { themes } from '../../services/constants';
import {
  detailCardSx,
  detailSectionLabelSx,
} from '../DesignSystem/RequestDetailField';
import {
  setNotifications,
  appendNotifications,
  markOneRead,
  markAllRead,
  setUnreadCount,
  setLoading,
  incrementPage,
} from '../../services/Redux/notificationReducer';
import {
  fetchNotifications,
  fetchUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
} from '../../services/utils/NotificationAPI';
import useNotificationSocket from '../../hooks/useNotificationSocket';
import {
  getNotificationIcon,
  formatNotificationTimestamp,
  isMention,
  humanizeEventType,
  formatFullTimestamp,
  groupNotificationsByDate,
  NOTIFICATION_DATE_GROUPS,
} from './notificationUtils';
import { getAccessBasedRoute } from '../../services/utils/accessedBasedRoute';

const selectDomain = (state) => state.tokenReducer?.domain;
const selectProductAccess = (state) => state.tokenReducer?.productAccess;
const selectNotifications = (state) => state.notifications?.notifications ?? [];
const selectUnreadCount = (state) => state.notifications?.unreadCount ?? 0;
const selectIsLoading = (state) => state.notifications?.isLoading ?? false;
const selectHasMore = (state) => state.notifications?.hasMore ?? true;
const selectPage = (state) => state.notifications?.page ?? 1;

// `open`/`onOpenChange` are controlled by Sidebar/index.js (the only place
// that renders this) so it can enforce "only one of notifications / profile
// side-peek open at a time" — see handleNotificationOpenChange there.
const NotificationBell = ({ open: panelOpen, onOpenChange }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedNotification, setSelectedNotification] = useState(null);

  const domain = useSelector(selectDomain);
  const productAccess = useSelector(selectProductAccess);
  const notifications = useSelector(selectNotifications);
  const unreadCount = useSelector(selectUnreadCount);
  const isLoading = useSelector(selectIsLoading);
  const hasMore = useSelector(selectHasMore);
  const page = useSelector(selectPage);

  useNotificationSocket();

  const refreshUnreadCount = useCallback(async () => {
    if (!domain) return;
    try {
      const res = await fetchUnreadCount(domain);
      if (res?.responseDetails?.unread_count !== undefined) {
        dispatch(setUnreadCount(res.responseDetails.unread_count));
      }
    } catch {
      // non-fatal
    }
  }, [domain, dispatch]);

  useEffect(() => {
    refreshUnreadCount();
    const interval = setInterval(refreshUnreadCount, 60000);
    const handleFocus = () => refreshUnreadCount();
    window.addEventListener('focus', handleFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [refreshUnreadCount]);

  const loadNotifications = useCallback(
    async (pageNum) => {
      if (!domain) return;
      dispatch(setLoading(true));
      try {
        const res = await fetchNotifications(domain, pageNum);
        if (res?.responseDetails) {
          if (pageNum === 1) {
            dispatch(setNotifications(res.responseDetails));
          } else {
            dispatch(appendNotifications(res.responseDetails));
          }
        } else {
          dispatch(setLoading(false));
        }
      } catch {
        dispatch(setLoading(false));
      }
    },
    [domain, dispatch],
  );

  // Was gated on notifications.length === 0, so the list only ever fetched
  // once per mount — the badge count keeps polling live via
  // refreshUnreadCount() below, but a notification created after that first
  // load (e.g. a leave application) never made it into the list, since
  // neither this effect nor a WebSocket event necessarily re-ran to add it.
  // Refetching page 1 on every open keeps the list honest with whatever
  // pushed the count up.
  useEffect(() => {
    if (panelOpen && !isLoading) {
      loadNotifications(1);
    }
  }, [panelOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // `open` can now go false from outside (Sidebar force-closes this panel on
  // a profile-peek open or a workspace module switch) without ever calling
  // handleClosePanel below, which otherwise owns clearing the detail view.
  // Without this, reopening after an external close could still show
  // whichever notification/expanded state was left over from before.
  useEffect(() => {
    if (!panelOpen) {
      setSelectedNotification(null);
      setExpanded(false);
    }
  }, [panelOpen]);

  const mentionsCount = useMemo(
    () => notifications.filter(isMention).length,
    [notifications],
  );

  const visibleNotifications = useMemo(() => {
    if (activeTab === 'unread') return notifications.filter((n) => !n.is_read);
    if (activeTab === 'mentions') return notifications.filter(isMention);
    return notifications;
  }, [notifications, activeTab]);

  const groupedVisibleNotifications = useMemo(
    () => groupNotificationsByDate(visibleNotifications),
    [visibleNotifications],
  );

  const handleLoadMore = () => {
    const nextPage = page + 1;
    dispatch(incrementPage());
    loadNotifications(nextPage);
  };

  const handleMarkRead = async (id) => {
    dispatch(markOneRead(id));
    try {
      await markNotificationRead(domain, id);
    } catch {
      // optimistic update already applied
    }
  };

  const handleMarkAllRead = async () => {
    dispatch(markAllRead());
    try {
      await markAllNotificationsRead(domain);
    } catch {
      // optimistic update already applied
    }
  };

  const getNotificationTargetRoute = (notif) => {
    const searchableText = `${notif?.title || ''} ${notif?.body || ''}`.toLowerCase();

    const isNewLocationNotification = /(new location added|added a new location|new site added|geo[- ]?fenced? sites?|geofence)/.test(
      searchableText,
    );
    if (isNewLocationNotification) {
      // Geo Fenced Sites lives under Team Workspace > Settings now (it was
      // a tab of People > Settings).
      const canOpenGeoFencedSites = productAccess?.some(
        (p) => p?.product_name === 'Team Workspace',
      );
      return canOpenGeoFencedSites
        ? "/admin/teamzone/settings/geo-fenced-sites"
        : "/admin/myzone/status";
    }

    const hasTeamZoneAccess = productAccess?.some(
      (p) => p?.product_name === 'Team Workspace',
    );

    const isHolidayOrWeekOff = /(holiday|week[- ]?off)/.test(searchableText);
    const holidayWeekOffTab = /worked/.test(searchableText) ? 'post-approval' : 'pre-approval';
    const isAdvanceSalary = /advance[- ]?salary/.test(searchableText);
    const isOutNotification = /(\bout\b|checkout reminder|check out)/i.test(searchableText) || notif?.event_type === 'attendance.checkout_reminder';

    if (isOutNotification) {
      // Checking out happens on Attendance, not on the Team Workspace
      // landing screen (Home), so this one stays pinned to Attendance.
      return hasTeamZoneAccess
        ? '/admin/teamzone/attendance'
        : getAccessBasedRoute('My Workspace');
    }

    if (/task/.test(searchableText)) {
      return '/admin/project/mytask';
    }

    // Attendance-type requests each have their own screen/tab. Resolve them
    // before the generic keyword branches below, which used to send all of
    // them to one shared "work-adjustment" view (Approve mapped that to
    // Attendance Correction; My Workspace opened whatever tab was last used).
    const isBreakRequest =
      /\bbreak\b/.test(searchableText) ||
      String(notif?.event_type || '').startsWith('attendance.break_');
    const isCompOffRequest = /comp[- ]?off/.test(searchableText);
    const isOffDayRequest = !isCompOffRequest && /off[- ]?day/.test(searchableText);
    const attendanceTabKey = isBreakRequest
      ? 'break'
      : isCompOffRequest
        ? 'compoff'
        : /permission/.test(searchableText)
          ? 'permission'
          : /on[- ]?duty/.test(searchableText)
            ? 'onduty'
            : null;

    if (attendanceTabKey && !isHolidayOrWeekOff) {
      return hasTeamZoneAccess
        ? `/admin/teamzone/approve?view=${attendanceTabKey}`
        : `/admin/myzone/apply?view=work-adjustment&tab=${attendanceTabKey}`;
    }

    if (isOffDayRequest && !isHolidayOrWeekOff) {
      return hasTeamZoneAccess
        ? '/admin/teamzone/approve?view=offday-duty'
        : '/admin/myzone/apply?view=offday-duty';
    }

    if (
      hasTeamZoneAccess &&
      /(leave|leave request|leave approval|leave rejected|leave approved|leave cancelled|pre[- ]?ot|preot|pre[- ]?overtime|pre[- ]?approval|post[- ]?ot|postot|post[- ]?overtime|post[- ]?approval|comp off|compoff|comp-off|off[- ]?day|offday|holiday|week[- ]?off|permission|on[- ]?duty|advance[- ]?salary|overtime|ot)/.test(searchableText)
    ) {
      if (/(leave|leave request|leave approval|leave rejected|leave approved|leave cancelled)/.test(searchableText)) {
        return '/admin/teamzone/approve?view=leave';
      }

      if (isAdvanceSalary) {
        return '/admin/teamzone/approve?view=advance-salary';
      }

      if (isHolidayOrWeekOff) {
        return `/admin/teamzone/approve?view=offday-duty&tab=${holidayWeekOffTab}`;
      }

      if (/(pre[- ]?ot|preot|pre[- ]?overtime|pre[- ]?approval|pre approval)/.test(searchableText)) {
        return '/admin/teamzone/approve?view=overtime&tab=pre-approval';
      }

      if (/(post[- ]?ot|postot|post[- ]?overtime|post[- ]?approval|post approval)/.test(searchableText)) {
        return '/admin/teamzone/approve?view=overtime&tab=post-approval';
      }

      if (/(comp off|compoff|comp-off|off[- ]?day|offday|permission|on[- ]?duty)/.test(searchableText)) {
        return '/admin/teamzone/approve?view=work-adjustment';
      }

      if (/(overtime|ot)/.test(searchableText)) {
        return '/admin/teamzone/approve?view=overtime&tab=post-approval';
      }

      return '/admin/teamzone/approve';
    }

    if (/(leave|leave request|leave approval|leave rejected|leave approved|leave cancelled)/.test(searchableText)) {
      return '/admin/myzone/apply?view=leave';
    }

    if (isAdvanceSalary) {
      return '/admin/myzone/apply?view=advance-salary';
    }

    if (isHolidayOrWeekOff) {
      return `/admin/myzone/apply?view=offday-duty&tab=${holidayWeekOffTab}`;
    }

    if (/(pre[- ]?ot|preot|pre[- ]?overtime|pre[- ]?approval|pre approval)/.test(searchableText)) {
      if (/(off[- ]?day|comp off|compoff|comp-off|permission|on[- ]?duty)/.test(searchableText)) {
        return '/admin/myzone/apply?view=work-adjustment';
      }
      return '/admin/myzone/apply?view=overtime&tab=pre-approval';
    }

    if (/(post[- ]?ot|postot|post[- ]?overtime|post[- ]?approval|post approval)/.test(searchableText)) {
      if (/(off[- ]?day|comp off|compoff|comp-off|permission|on[- ]?duty)/.test(searchableText)) {
        return '/admin/myzone/apply?view=work-adjustment';
      }
      return '/admin/myzone/apply?view=overtime&tab=post-approval';
    }

    if (/(comp off|compoff|comp-off|off[- ]?day|offday)/.test(searchableText)) {
      return '/admin/myzone/apply?view=work-adjustment';
    }

    if (/(permission|on[- ]?duty)/.test(searchableText)) {
      return '/admin/myzone/apply?view=work-adjustment';
    }

    if (/(overtime|ot)/.test(searchableText)) {
      return '/admin/myzone/apply?view=overtime&tab=post-approval';
    }

    const targetUrl = notif?.target_url;
    if (typeof targetUrl === 'string' && targetUrl.trim()) {
      const normalizedTarget = targetUrl.trim();
      if (normalizedTarget.startsWith('/admin/')) {
        return normalizedTarget;
      }
    }

    return null;
  };

  const handleNotificationClick = (notif) => {
    if (!notif.is_read) {
      handleMarkRead(notif.id);
    }
    setSelectedNotification(notif);
  };

  const renderNotificationItem = (notif) => {
    const { icon: IconComp, bg, fg } = getNotificationIcon(notif.event_type);
    return (
      <ListItem
        key={notif.id}
        alignItems="flex-start"
        onClick={() => handleNotificationClick(notif)}
        sx={{
          cursor: 'pointer',
          bgcolor: notif.is_read ? 'transparent' : 'action.hover',
          '&:hover': { bgcolor: 'action.selected' },
          py: 1.5,
          px: 2,
          gap: 1.5,
        }}
      >
        <Avatar sx={{ bgcolor: bg, color: fg, width: 40, height: 40, flexShrink: 0 }}>
          <IconComp fontSize="small" />
        </Avatar>
        <ListItemText
          primary={
            <Typography
              variant="body2"
              fontWeight={notif.is_read ? designWeights.regular : designWeights.semibold}
              sx={{ lineHeight: 1.4 }}
            >
              {notif.title}
            </Typography>
          }
          secondary={
            <Typography
              variant="caption"
              color="text.secondary"
              component="span"
              display="block"
              sx={{ mt: 0.25, lineHeight: 1.4 }}
            >
              {notif.body}
            </Typography>
          }
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            flexShrink: 0,
            mt: 0.4,
          }}
        >
          <Typography variant="caption" color="text.disabled" whiteSpace="nowrap">
            {formatNotificationTimestamp(notif.created_at)}
          </Typography>
          {!notif.is_read && (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'success.main',
                flexShrink: 0,
              }}
            />
          )}
        </Box>
      </ListItem>
    );
  };

  const handleBackToList = () => setSelectedNotification(null);

  const handleClosePanel = () => {
    onOpenChange(false);
    setSelectedNotification(null);
    setExpanded(false);
  };

  const handleDetailNavigate = () => {
    const targetRoute = selectedNotification
      ? getNotificationTargetRoute(selectedNotification)
      : null;
    if (targetRoute) {
      history.push(targetRoute);
    }
    handleClosePanel();
  };

  // Used to navigate to a separate /notifications/all page (NotificationHistory.js)
  // that hand-rolled the exact same list/tabs/detail view this panel already
  // has, just without any design tokens. Expanding this panel in place reuses
  // the one, already-correct implementation instead of keeping two.
  const handleViewAll = () => {
    setExpanded(true);
  };

  const tabLabel = (label, count) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <span>{label}</span>
      <Box
        sx={{
          minWidth: 20,
          height: 20,
          px: 0.5,
          borderRadius: '999px',
          bgcolor: activeTab === label.toLowerCase() ? 'success.main' : 'action.selected',
          color: activeTab === label.toLowerCase() ? 'success.contrastText' : 'text.secondary',
          fontSize: 12,
          fontWeight: designWeights.semibold,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {count}
      </Box>
    </Box>
  );

  const detailTargetRoute = selectedNotification
    ? getNotificationTargetRoute(selectedNotification)
    : null;

  const detailHeader = selectedNotification ? (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
      <IconButton onClick={handleBackToList} size="small" sx={{ color: designColors.slate600 }}>
        <ArrowBackIcon fontSize="small" />
      </IconButton>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: designWeights.heading,
            color: designColors.slate800,
            lineHeight: 1.3,
          }}
        >
          Notification
        </Typography>
        <Typography
          sx={{ mt: 0.25, fontSize: 13, color: designColors.slate600 }}
        >
          {humanizeEventType(selectedNotification.event_type)}
        </Typography>
      </Box>
    </Stack>
  ) : undefined;

  const listHeaderActions =
    !selectedNotification && unreadCount > 0 ? (
      <Typography
        onClick={handleMarkAllRead}
        sx={{
          fontSize: 13,
          color: 'success.main',
          cursor: 'pointer',
          fontWeight: designWeights.medium,
          whiteSpace: 'nowrap',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        Mark all as read
      </Typography>
    ) : undefined;

  const detailFooter = selectedNotification ? (
    <Stack direction="row" spacing={1.25} justifyContent="flex-end">
      <SecondaryButton onClick={handleBackToList}>Back</SecondaryButton>
      {detailTargetRoute ? (
        <PrimaryButton onClick={handleDetailNavigate}>View Details</PrimaryButton>
      ) : null}
    </Stack>
  ) : null;

  const listFooter = !selectedNotification && !expanded ? (
    <Box
      onClick={handleViewAll}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        cursor: 'pointer',
        color: 'success.main',
      }}
    >
      <NotificationsActiveOutlinedIcon fontSize="small" />
      <Typography
        variant="body2"
        fontWeight={designWeights.medium}
      >
        View all notifications
      </Typography>
      <ChevronRightIcon fontSize="small" />
    </Box>
  ) : null;

  // The bell lives in Sidebar/index.js's AppBar, which is mounted once and
  // persists across every route — unlike Approve/index.js etc. it isn't a
  // descendant of any screen's own <ThemeProvider theme={designTheme}>, so it
  // can't just inherit Poppins from context. Wrapped unconditionally (not
  // gated on the current route, unlike the AppBar's own company/user name
  // text) to match sharedPanelTextUi/RequestDetailField.js — the shared
  // panel-text styles every Approve/My Zone detail panel uses — which set
  // designFontFamily and designWeights unconditionally wherever they're
  // used, since notifications are one feature and shouldn't re-skin
  // depending on which page happened to be open underneath when opened.
  // withDesignFont (not the bare designTheme) keeps this panel's palette —
  // success.main, action.hover, the error badge — intact.
  const content = (
    <>
      <Tooltip title="Notifications">
        <IconButton
          onClick={() => onOpenChange(true)}
          size="small"
          aria-label={
            unreadCount > 0
              ? `${unreadCount} unread notifications`
              : 'Notifications'
          }
        >
          <Badge
            badgeContent={unreadCount > 99 ? '99+' : unreadCount}
            color="error"
            invisible={unreadCount === 0}
          >
            <NotificationsNoneOutlinedIcon sx={{ color: themes.headLine }} />
          </Badge>
        </IconButton>
      </Tooltip>

      {/* No width override — the shared GlobalSidePanel default
          (PANEL_DRAWER_WIDTH, 620) is what every other detail/create panel
          in the app (e.g. Approve's Apply.js screens) already uses. */}
      <GlobalSidePanel
        open={panelOpen}
        onClose={handleClosePanel}
        expanded={expanded}
        onToggleExpand={() => setExpanded((prev) => !prev)}
        title={selectedNotification ? undefined : 'Notifications'}
        header={detailHeader}
        headerActions={listHeaderActions}
        footer={selectedNotification ? detailFooter : listFooter}
        bodySx={{ display: 'flex', flexDirection: 'column', p: 0 }}
      >
        {selectedNotification ? (
          (() => {
            const { icon: IconComp, bg, fg } = getNotificationIcon(
              selectedNotification.event_type,
            );
            return (
              <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Avatar sx={{ bgcolor: bg, color: fg, width: 48, height: 48 }}>
                    <IconComp fontSize="medium" />
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 16,
                        fontWeight: designWeights.heading,
                        color: designColors.slate900,
                        lineHeight: 1.35,
                      }}
                    >
                      {selectedNotification.title}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      sx={{ mt: 0.6, rowGap: 0.5 }}
                    >
                      <Typography
                        sx={{ fontSize: 12.5, color: designColors.slate500 }}
                      >
                        {formatFullTimestamp(selectedNotification.created_at)}
                      </Typography>
                      {!selectedNotification.is_read ? (
                        <Chip
                          label="Unread"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: 11,
                            fontWeight: designWeights.heading,
                            bgcolor: designColors.tealTint,
                            color: designColors.tealDark,
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      ) : null}
                    </Stack>
                  </Box>
                </Stack>

                <Box sx={detailCardSx}>
                  <Typography sx={detailSectionLabelSx}>Message</Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: designColors.slate800,
                      lineHeight: 1.6,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {selectedNotification.body || 'No additional details for this notification.'}
                  </Typography>
                </Box>
              </Box>
            );
          })()
        ) : (
          <>
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              variant="fullWidth"
              sx={{ px: 2.5, minHeight: 40, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}
            >
              <Tab value="all" label={tabLabel('All', notifications.length)} sx={{ minHeight: 40 }} />
              <Tab value="unread" label={tabLabel('Unread', unreadCount)} sx={{ minHeight: 40 }} />
              <Tab value="mentions" label={tabLabel('Mentions', mentionsCount)} sx={{ minHeight: 40 }} />
            </Tabs>

            <Box sx={{ overflowY: 'auto', flex: 1 }}>
              {visibleNotifications.length === 0 && !isLoading ? (
                <EmptyState
                  title={
                    activeTab === 'unread'
                      ? "You're all caught up!"
                      : activeTab === 'mentions'
                        ? 'No mentions yet'
                        : 'No notifications yet'
                  }
                  subtitle={
                    activeTab === 'unread'
                      ? "You've read everything for now — new notifications will show up here."
                      : activeTab === 'mentions'
                        ? "You'll see it here whenever someone mentions you."
                        : "We'll let you know when something new comes in."
                  }
                  size={104}
                  sx={{ minHeight: 240 }}
                />
              ) : (
                <List disablePadding>
                  {NOTIFICATION_DATE_GROUPS.flatMap((groupLabel) => {
                    const groupItems = groupedVisibleNotifications[groupLabel];
                    if (groupItems.length === 0) return [];
                    return [
                      <Typography
                        key={`group-${groupLabel}`}
                        sx={{
                          px: 2,
                          pt: 1.5,
                          pb: 0.5,
                          fontSize: 11.5,
                          fontWeight: designWeights.semibold,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          color: designColors.slate500,
                        }}
                      >
                        {groupLabel}
                      </Typography>,
                      ...groupItems.map(renderNotificationItem),
                    ];
                  })}
                </List>
              )}

              {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {!isLoading && hasMore && activeTab === 'all' && notifications.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    py: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Button onClick={handleLoadMore} size="small" variant="text">
                    Load more
                  </Button>
                </Box>
              )}
            </Box>
          </>
        )}
      </GlobalSidePanel>
    </>
  );

  return <ThemeProvider theme={withDesignFont}>{content}</ThemeProvider>;
};

export default NotificationBell;
